import { createSlice } from '@reduxjs/toolkit';
import { createSelector } from 'reselect';
import { getRowIds, getRowIdsInRange } from '../../table/redux/rowRegistrySlice';

/**
 * @property selection - Map of row IDs to their selection state
 * @property querySelected - Whether the entire query result is selected
 * @property anchorRowIndex - Tracks the most recently selected row index to enable
 *   shift-click range selection. When a user shift-clicks a row, all rows between
 *   the anchor and the clicked row are selected.
 */

const initialState = {
  selection: {},
  querySelected: false,
  anchorRowIndex: null
};
const selectionSlice = createSlice({
  name: 'selection',
  initialState,
  reducers: {
    setRowsSelected: (state, action) => {
      action.payload.forEach(({
        rowId,
        selected
      }) => {
        state.selection[rowId] = selected;
      });
      state.querySelected = false;
    },
    transitionFromQueryToIds: (state, action) => {
      const {
        deselectedRowId,
        visibleRowIds,
        newAnchorIndex
      } = action.payload;
      visibleRowIds.forEach(rowId => {
        state.selection[rowId] = true;
      });
      state.selection[deselectedRowId] = false;
      state.querySelected = false;
      state.anchorRowIndex = newAnchorIndex;
    },
    selectQuery: state => {
      state.querySelected = true;
    },
    clearSelection: state => {
      state.selection = {};
      state.querySelected = false;
      state.anchorRowIndex = null;
    },
    unselectObjectsNoLongerInTable: (state, action) => {
      const newRowIdsInTable = new Set(action.payload);
      const previouslySelectedRowIds = Object.keys(state.selection);
      state.selection = previouslySelectedRowIds.reduce((acc, rowId) => {
        if (newRowIdsInTable.has(rowId)) {
          acc[rowId] = state.selection[rowId];
        }
        return acc;
      }, {});
      state.anchorRowIndex = null;
    },
    handleCellClicked: (state, action) => {
      action.payload.rowsToUpdate.forEach(({
        rowId,
        selected
      }) => {
        state.selection[rowId] = selected;
      });
      state.anchorRowIndex = action.payload.newAnchorIndex;
      state.querySelected = false;
    }
  }
});
export const selectionReducer = selectionSlice.reducer;
export const {
  setRowsSelected,
  transitionFromQueryToIds,
  selectQuery,
  clearSelection,
  unselectObjectsNoLongerInTable,
  handleCellClicked
} = selectionSlice.actions;
export const getSelectionState = ({
  selection
}) => selection;
export const getIsQuerySelected = createSelector([getSelectionState], ({
  querySelected
}) => querySelected);
export const getSelectedRowIds = createSelector([getSelectionState], ({
  selection
}) => new Set(Object.entries(selection).filter(([__rowId, selected]) => selected).map(([rowId]) => rowId)));
export const getSelectionCount = createSelector([getSelectedRowIds], selectedRowIds => selectedRowIds.size);
export const getShouldShowBulkActionsContainer = createSelector([getSelectionCount], count => count > 0);
export const makeGetIsRowSelected = rowId => createSelector([getSelectedRowIds], selectedIds => selectedIds.has(rowId));
export const getAnchorRowIndex = createSelector([getSelectionState], ({
  anchorRowIndex
}) => anchorRowIndex);
export const cellClicked = payload => (dispatch, getState) => {
  const {
    rowIndex,
    rowId,
    checked,
    isShiftClick,
    isInfiniteScroll
  } = payload;
  const state = getState();
  if (isInfiniteScroll && !checked && getIsQuerySelected(state)) {
    dispatch(transitionFromQueryToIds({
      deselectedRowId: rowId,
      visibleRowIds: getRowIds(state),
      newAnchorIndex: rowIndex
    }));
    return;
  }
  const anchorIndex = getAnchorRowIndex(state);
  let rowsToUpdate;
  if (isShiftClick && anchorIndex !== null) {
    const rowIdsInRange = getRowIdsInRange(state, anchorIndex, rowIndex);
    rowsToUpdate = rowIdsInRange.map(id => ({
      rowId: id,
      selected: checked
    }));
  } else {
    rowsToUpdate = [{
      rowId,
      selected: checked
    }];
  }
  dispatch(handleCellClicked({
    rowsToUpdate,
    newAnchorIndex: rowIndex
  }));
};